import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import Modal from '../../../components/common/Modal'
import Button from '../../../components/common/Button'
import Input from '../../../components/common/Input'
import filesService from '../../../services/files'
import { analysesApi } from '../api'

/**
 * Modal for creating a new analysis
 * Allows user to select project files and create analysis
 * @param {boolean} isOpen - Modal open state
 * @param {Function} onClose - Close callback
 * @param {string} projectId - Project UUID
 * @param {string} projectName - Project name for display
 */
function CreateAnalysisModal({ isOpen, onClose, projectId, projectName }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [analysisType, setAnalysisType] = useState('synchro_parser')
  const [files, setFiles] = useState([])
  const [selectedFileIds, setSelectedFileIds] = useState([])
  const [loading, setLoading] = useState(false)
  const [filesLoading, setFilesLoading] = useState(false)
  const [error, setError] = useState('')

  const loadFiles = useCallback(async () => {
    try {
      setFilesLoading(true)
      const data = await filesService.listFiles(projectId, false)
      const fileList = data.results || data

      // Filter to only show completed uploaded files (exclude generated files)
      const completedFiles = fileList.filter(
        (f) => f.status === 'completed' && f.category === 'uploaded'
      )
      setFiles(completedFiles)

      // Auto-select all files by default
      const allFileIds = completedFiles.map((f) => f.id)
      setSelectedFileIds(allFileIds)

      if (completedFiles.length === 0) {
        setError('No completed files available. Please upload files first.')
      }
    } catch (err) {
      console.error('Error loading files:', err)
      toast.error('Failed to load project files')
      setError('Failed to load files')
    } finally {
      setFilesLoading(false)
    }
  }, [projectId])

  // Load project files when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      loadFiles()
    }
  }, [isOpen, projectId, loadFiles])

  const handleFileToggle = (fileId) => {
    setSelectedFileIds((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    )
  }

  const handleCreate = async () => {
    // Validation
    if (!name.trim()) {
      toast.error('Please enter an analysis name')
      return
    }

    // Volume figure generator doesn't need input files
    if (analysisType !== 'volume_figure_generator' && selectedFileIds.length === 0) {
      toast.error('Please select at least one file')
      return
    }

    try {
      setLoading(true)
      setError('')

      const response = await analysesApi.create({
        project: projectId,
        analysis_type: analysisType,
        name: name.trim(),
        input_file_ids: analysisType === 'volume_figure_generator' ? [] : selectedFileIds,
      })

      const analysisId = response.data?.id

      if (!analysisId) {
        console.error('No analysis ID in response:', response.data)
        toast.error('Analysis created but unable to navigate to detail page')
        onClose()
        return
      }

      toast.success('Analysis created successfully')
      onClose()

      // Navigate to analysis detail page
      navigate(`/analyses/${analysisId}`)
    } catch (err) {
      console.error('Error creating analysis:', err)
      const errorMsg =
        err.response?.data?.error || err.response?.data?.detail || 'Failed to create analysis'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setName('')
    setAnalysisType('synchro_parser')
    setSelectedFileIds([])
    setError('')
    onClose()
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        onClick={handleCreate}
        disabled={
          loading ||
          !name.trim() ||
          (analysisType !== 'volume_figure_generator' && selectedFileIds.length === 0)
        }
      >
        {loading ? 'Creating...' : 'Create Analysis'}
      </Button>
    </>
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Analysis" footer={footer}>
      <div className="space-y-4">
        {/* Project info */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <div className="text-sm text-gray-600">{projectName}</div>
        </div>

        {/* Analysis name */}
        <div>
          <label htmlFor="analysis-name" className="block text-sm font-medium text-gray-700 mb-1">
            Analysis Name *
          </label>
          <Input
            id="analysis-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., AM Peak Analysis"
            disabled={loading}
          />
        </div>

        {/* Analysis type selector */}
        <div>
          <label htmlFor="analysis-type" className="block text-sm font-medium text-gray-700 mb-1">
            Analysis Type *
          </label>
          <select
            id="analysis-type"
            value={analysisType}
            onChange={(e) => setAnalysisType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={loading}
          >
            <option value="synchro_parser">Analysis Table Generator</option>
            <option value="volume_figure_generator">Volume Figure Generator</option>
          </select>
          {analysisType === 'volume_figure_generator' && (
            <p className="mt-1 text-xs text-gray-500">
              No input files needed - you&apos;ll enter data manually
            </p>
          )}
        </div>

        {/* File selection - only show for analysis types that need files */}
        {analysisType !== 'volume_figure_generator' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Input Files *
            </label>

            {filesLoading ? (
              <div className="text-center py-4 text-gray-500">Loading files...</div>
            ) : files.length === 0 ? (
              <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded p-3">
                No completed files available. Please upload and process files first.
              </div>
            ) : (
              <div className="border border-gray-300 rounded max-h-60 overflow-y-auto">
                {files.map((file) => (
                  <label
                    key={file.id}
                    className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-200 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFileIds.includes(file.id)}
                      onChange={() => handleFileToggle(file.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={loading}
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {file.original_filename}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(file.file_size / 1024).toFixed(1)} KB • {file.mime_type}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </div>
        )}

        {/* Help text */}
        <div className="text-xs text-gray-500">
          The analysis will be created in &quot;configuring&quot; state. You&apos;ll need to start
          it manually from the detail page.
        </div>
      </div>
    </Modal>
  )
}

export default CreateAnalysisModal
